import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";
import { useCart } from "@/hooks/useCart";
import { supabase, formatPrice } from "@/lib/supabase";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";

interface Form {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country: string;
  state: string;
  city: string;
  zip: string;
  address: string;
  apt: string;
  customer_notes: string;
}

const emptyForm: Form = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  country: "Italy",
  state: "",
  city: "",
  zip: "",
  address: "",
  apt: "",
  customer_notes: "",
};

const DEMO_PRESETS: Form[] = [
  {
    first_name: "Sofia",
    last_name: "Rossi",
    email: "sofia.rossi@example.it",
    phone: "+39 331 4521 908",
    country: "Italy",
    state: "Lombardia",
    city: "Milano",
    zip: "20143",
    address: "Via Solari 27",
    apt: "Interno 4B",
    customer_notes: "Please add a handwritten card — it's a birthday gift for my niece.",
  },
  {
    first_name: "Léa",
    last_name: "Moreau",
    email: "lea.moreau@example.fr",
    phone: "+33 6 42 18 55 09",
    country: "France",
    state: "Île-de-France",
    city: "Paris",
    zip: "75011",
    address: "12 Rue Oberkampf",
    apt: "3e étage",
    customer_notes: "Gift wrap in cream ribbon, please. No invoice inside.",
  },
  {
    first_name: "Emma",
    last_name: "Bennett",
    email: "emma.bennett@example.com",
    phone: "+1 (415) 502-7188",
    country: "United States",
    state: "California",
    city: "San Francisco",
    zip: "94110",
    address: "482 Guerrero Street",
    apt: "Apt 5",
    customer_notes: "",
  },
];

export default function CheckoutPage() {
  const { lines, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fillDemo = () => {
    const next = DEMO_PRESETS[Math.floor(Math.random() * DEMO_PRESETS.length)];
    setForm(next);
    setError(null);
  };

  const currency = lines[0]?.currency ?? "EUR";
  const shipping = subtotal >= 80 || subtotal === 0 ? 0 : 6.9;
  const total = subtotal + shipping;

  if (lines.length === 0 && !submitting) {
    return (
      <main style={{ background: "#FFFFFF", minHeight: "100vh", fontFamily: "'Inter Tight', sans-serif" }}>
        <ShopNav variant="light" />
        <section style={{ padding: "200px 32px 160px", textAlign: "center", maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)", marginBottom: 18 }}>
            (EMPTY BASKET)
          </div>
          <h1
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 64,
              lineHeight: 0.95,
              letterSpacing: "-2px",
              color: TEXT_COLOR,
              margin: "0 0 22px",
              fontWeight: 400,
            }}
          >
            Nothing to wrap yet.
          </h1>
          <p style={{ fontSize: 14, color: "rgba(84,84,84,0.65)", marginBottom: 28, lineHeight: 1.7 }}>
            Come back with a little friend in hand — they're waiting on the shelf.
          </p>
          <Link
            to="/catalog"
            className="cursor-pointer whitespace-nowrap inline-flex items-center"
            style={{
              background: TEXT_COLOR,
              color: "#FFFFFF",
              borderRadius: 999,
              padding: "14px 28px",
              fontSize: 12,
              letterSpacing: "2px",
              textDecoration: "none",
              gap: 8,
            }}
          >
            BROWSE THE COLLECTION
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-line" style={{ fontSize: 14 }}></i>
            </span>
          </Link>
        </section>
        <ShopFooter />
      </main>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!form.first_name.trim() || !form.email.trim() || !form.address.trim() || !form.city.trim()) {
      setError("Please fill in your name, email, address and city.");
      return;
    }

    setSubmitting(true);
    try {
      const recipient = {
        name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        country: form.country.trim(),
        state: form.state.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
        address: form.apt ? `${form.address.trim()}, ${form.apt.trim()}` : form.address.trim(),
      };

      const { data: order, error: orderErr } = await supabase
        .from("order_headers")
        .insert({
          currency,
          payment_provider: "mock",
          status: "paid",
          subtotal_items: subtotal,
          shipping_total: shipping,
          customer_notes: form.customer_notes.trim() || null,
          customer_id: null,
          recipient,
        })
        .select("id")
        .single();

      if (orderErr) throw orderErr;
      if (!order) throw new Error("Order creation failed");

      const items = lines.map((l) => ({
        order_id: order.id,
        product_id: String(l.productId),
        product_name: l.name,
        quantity: l.quantity,
        unit_price: l.unitPrice,
        final_price: l.unitPrice,
        subtotal: l.unitPrice * l.quantity,
      }));

      const { error: itemsErr } = await supabase.from("order_items").insert(items);
      if (itemsErr) throw itemsErr;

      clearCart();
      navigate(`/order-success/${order.id}`, { replace: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Hero-style headline block */}
      <section style={{ padding: "140px 40px 40px", maxWidth: 1280, margin: "0 auto", position: "relative" }}>
        <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (02)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
            CHECKOUT · STEP 2 OF 3
          </span>
        </div>
        <motion.h1
          initial={{ opacity: 0, filter: "blur(12px)", y: 12 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
            fontWeight: 600,
            fontSize: 92,
            lineHeight: 0.95,
            letterSpacing: "-2.4px",
            color: TEXT_COLOR,
            margin: 0,
          }}
        >
          Almost{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-3.8px",
            }}
          >
            yours.
          </span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          className="flex items-end justify-between flex-wrap"
          style={{ gap: 20, marginTop: 20 }}
        >
          <p
            style={{
              fontSize: 14,
              color: "rgba(84,84,84,0.65)",
              maxWidth: 460,
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            A few details and your little companion will be packed with care, ready to travel your way.
          </p>

          {/* One-click demo fill */}
          <div
            className="flex items-center"
            style={{
              gap: 14,
              border: `1px dashed ${BORDER_MID}`,
              borderRadius: 999,
              padding: "8px 8px 8px 20px",
              background: "#FBFAF7",
            }}
          >
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 16,
                color: "rgba(84,84,84,0.7)",
                letterSpacing: "-0.2px",
                whiteSpace: "nowrap",
              }}
            >
              in a hurry?
            </span>
            <button
              type="button"
              onClick={fillDemo}
              className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85 inline-flex items-center"
              style={{
                gap: 8,
                background: TEXT_COLOR,
                color: "#FFFFFF",
                border: 0,
                borderRadius: 999,
                padding: "9px 18px",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "2px",
              }}
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-magic-line" style={{ fontSize: 12 }}></i>
              </span>
              AUTO-FILL DEMO
            </button>
          </div>
        </motion.div>
      </section>

      <form onSubmit={handleSubmit} style={{ padding: "20px 40px 120px", maxWidth: 1280, margin: "0 auto" }}>
        <div
          className="grid"
          style={{ gridTemplateColumns: "1.5fr 1fr", gap: 80, alignItems: "start" }}
        >
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
          >
            <SectionTitle number="01" title="Contact" subtitle="Where should we send the confirmation?" />
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <Field label="First name" name="first_name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
              <Field label="Last name" name="last_name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field label="Phone" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>

            <div style={{ height: 60 }} />

            <SectionTitle number="02" title="Shipping address" subtitle="Where the little one is heading." />
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <Field label="Country" name="country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
              <Field label="State / Region" name="state" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
              <Field label="City" name="city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} required />
              <Field label="ZIP / Postal code" name="zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Street address" name="address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} required />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Apartment, suite (optional)" name="apt" value={form.apt} onChange={(v) => setForm({ ...form, apt: v })} />
              </div>
            </div>

            <div style={{ height: 60 }} />

            <SectionTitle number="03" title="Note for the atelier" subtitle="Gift wrap, a handwritten card, anything special." />
            <div>
              <textarea
                name="customer_notes"
                value={form.customer_notes}
                onChange={(e) => setForm({ ...form, customer_notes: e.target.value.slice(0, 500) })}
                maxLength={500}
                placeholder="Tell us how to make it feel like magic."
                rows={4}
                style={{
                  width: "100%",
                  border: `1px solid ${BORDER_SOFT}`,
                  borderBottom: `1px solid ${BORDER_MID}`,
                  background: "#FFFFFF",
                  borderRadius: 0,
                  padding: "16px 2px",
                  fontFamily: "'Inter Tight', sans-serif",
                  fontSize: 14,
                  color: TEXT_COLOR,
                  resize: "vertical",
                  outline: "none",
                }}
              />
              <div style={{ fontSize: 11, color: "rgba(84,84,84,0.5)", marginTop: 8, textAlign: "right", letterSpacing: "1px" }}>
                {form.customer_notes.length} / 500
              </div>
            </div>
          </motion.div>

          {/* Order summary */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.28 }}
            style={{
              background: "#FFFFFF",
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: 4,
              padding: "36px 32px",
              position: "sticky",
              top: 110,
            }}
          >
            <div className="flex items-baseline justify-between" style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 32,
                  color: TEXT_COLOR,
                  letterSpacing: "-0.8px",
                  fontWeight: 400,
                }}
              >
                Your order
              </div>
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 18,
                  color: "rgba(84,84,84,0.5)",
                }}
              >
                ({lines.length})
              </span>
            </div>
            <div style={{ fontSize: 11, letterSpacing: "2px", color: "rgba(84,84,84,0.5)", marginBottom: 24 }}>
              PACKED WITH CARE
            </div>

            <div style={{ maxHeight: 280, overflowY: "auto", paddingRight: 4, marginBottom: 6 }}>
              {lines.map((l, idx) => (
                <div
                  key={l.productId}
                  className="flex"
                  style={{
                    gap: 14,
                    padding: "14px 0",
                    alignItems: "center",
                    borderBottom: idx === lines.length - 1 ? "none" : `1px dashed ${BORDER_SOFT}`,
                  }}
                >
                  <div style={{ position: "relative", width: 60, height: 76, flexShrink: 0 }}>
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#F6F4EF",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={l.image}
                        alt={l.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                      />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        top: -7,
                        right: -7,
                        background: TEXT_COLOR,
                        color: "#FFFFFF",
                        borderRadius: 999,
                        minWidth: 20,
                        height: 20,
                        fontSize: 10,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 6px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {l.quantity}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 18,
                        color: TEXT_COLOR,
                        lineHeight: 1.15,
                        letterSpacing: "-0.3px",
                        fontWeight: 400,
                      }}
                    >
                      {l.name.split("—")[0]?.trim() || l.name}
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(84,84,84,0.55)", marginTop: 4, letterSpacing: "0.5px" }}>
                      {formatPrice(l.unitPrice, l.currency)} × {l.quantity}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: TEXT_COLOR, whiteSpace: "nowrap", fontWeight: 500 }}>
                    {formatPrice(l.unitPrice * l.quantity, l.currency)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: BORDER_SOFT, margin: "18px 0" }} />

            <SummaryRow label="Subtotal" value={formatPrice(subtotal, currency)} />
            <SummaryRow
              label="Shipping"
              value={shipping === 0 ? "Complimentary" : formatPrice(shipping, currency)}
              italic={shipping === 0}
            />

            <div style={{ height: 1, background: BORDER_SOFT, margin: "20px 0 18px" }} />

            <div className="flex items-baseline justify-between">
              <span style={{ fontSize: 12, letterSpacing: "2.5px", color: TEXT_COLOR }}>TOTAL</span>
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 40,
                  color: TEXT_COLOR,
                  letterSpacing: "-1px",
                  fontWeight: 400,
                  lineHeight: 1,
                }}
              >
                {formatPrice(total, currency)}
              </span>
            </div>

            {error && (
              <div
                style={{
                  marginTop: 20,
                  padding: "12px 14px",
                  background: "#FFF4F2",
                  border: "1px solid #F1CFC9",
                  color: "#B14837",
                  borderRadius: 4,
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="cursor-pointer whitespace-nowrap w-full transition-opacity hover:opacity-85"
              style={{
                marginTop: 26,
                background: TEXT_COLOR,
                color: "#FFFFFF",
                border: 0,
                borderRadius: 999,
                padding: "18px 24px",
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: "2.5px",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "PROCESSING…" : `PLACE ORDER — ${formatPrice(total, currency)}`}
            </button>

            <div className="flex items-center justify-center" style={{ gap: 8, marginTop: 16 }}>
              <div className="w-3 h-3 flex items-center justify-center" style={{ color: "rgba(84,84,84,0.55)" }}>
                <i className="ri-lock-2-line" style={{ fontSize: 11 }}></i>
              </div>
              <span
                style={{
                  fontSize: 10,
                  color: "rgba(84,84,84,0.55)",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                }}
              >
                Secure demo checkout · no real card charged
              </span>
            </div>
          </motion.aside>
        </div>
      </form>

      {/* Editorial watermark footer accent */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 28,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)" }}>
          MAISON DES JOUETS · ATELIER
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — made slowly, with love
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}

function SectionTitle({
  number,
  title,
  subtitle,
}: {
  number: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div className="flex items-baseline" style={{ gap: 14, marginBottom: 6 }}>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
            fontWeight: 400,
          }}
        >
          ({number})
        </span>
        <span
          style={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: "-0.8px",
            color: TEXT_COLOR,
          }}
        >
          {title}
        </span>
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "rgba(84,84,84,0.55)", marginLeft: 38, letterSpacing: "0.2px" }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}

function Field({ label, name, value, onChange, type = "text", required }: FieldProps) {
  const [focused, setFocused] = useState(false);
  return (
    <label style={{ display: "block", position: "relative" }}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: "2px",
          color: "rgba(84,84,84,0.55)",
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        {label}
        {required && (
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              marginLeft: 6,
              color: TEXT_COLOR,
            }}
          >
            *
          </span>
        )}
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        required={required}
        style={{
          width: "100%",
          border: 0,
          borderBottom: `1px solid ${focused ? TEXT_COLOR : BORDER_MID}`,
          background: "transparent",
          borderRadius: 0,
          padding: "10px 2px 12px",
          fontFamily: "'Inter Tight', sans-serif",
          fontSize: 15,
          color: TEXT_COLOR,
          outline: "none",
          transition: "border-color 0.25s ease",
        }}
      />
    </label>
  );
}

function SummaryRow({ label, value, italic }: { label: string; value: string; italic?: boolean }) {
  return (
    <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
      <span style={{ fontSize: 13, color: "rgba(84,84,84,0.75)" }}>{label}</span>
      <span
        style={{
          fontSize: italic ? 15 : 14,
          color: TEXT_COLOR,
          fontWeight: italic ? 400 : 500,
          fontFamily: italic ? "'Instrument Serif', serif" : "'Inter Tight', sans-serif",
          fontStyle: italic ? "italic" : "normal",
          letterSpacing: italic ? "-0.3px" : "0",
        }}
      >
        {value}
      </span>
    </div>
  );
}